import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import LogoMark from "../components/LogoMark";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { signIn, completeTwoFactor } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await signIn(email, password);
      if (res?.twoFactorRequired) {
        setTicket(res.ticket);
        setSubmitting(false);
        return;
      }
      navigate("/home", { replace: true });
    } catch (e2) {
      setError(formatApiError(e2));
      setSubmitting(false);
    }
  };

  const verify2fa = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await completeTwoFactor(ticket, code);
      navigate("/home", { replace: true });
    } catch (e2) {
      setError(formatApiError(e2));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-[#050d1a] relative">
      <div className="csc-grid-bg absolute inset-0 opacity-30" />
      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8"><LogoMark size={48} withText /></div>

        {!ticket ? (
          <form onSubmit={submit} className="csc-card p-8 space-y-5" data-testid="login-form">
            <div>
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#00aaff]">Sign In</div>
              <h1 className="text-2xl lg:text-3xl font-semibold mt-1">Welcome back</h1>
              <p className="text-sm text-[#94a3b8] mt-2">Continue your mission. Securely.</p>
            </div>
            <div>
              <label className="csc-label">Email</label>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="csc-input" data-testid="login-email" />
            </div>
            <div>
              <label className="csc-label">Password</label>
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="csc-input" data-testid="login-password" />
            </div>
            {error && <div className="text-sm text-red-400" data-testid="login-error">{error}</div>}
            <button disabled={submitting} className="csc-btn-primary w-full" data-testid="login-submit">
              {submitting ? "Signing in…" : "Sign In"}
            </button>
            <div className="text-center text-xs font-mono-tech">
              <Link to="/forgot-password" className="csc-link" data-testid="login-forgot-password">Forgot your password?</Link>
            </div>
            <p className="text-center text-xs font-mono-tech text-[#94a3b8]">
              New here?{" "}
              <Link to="/" className="csc-link" data-testid="login-to-register">Register an account →</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={verify2fa} className="csc-card p-8 space-y-5" data-testid="login-2fa-form">
            <div className="flex items-center gap-3">
              <ShieldCheck size={24} className="text-[#39ff14]" />
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">Two-Factor Required</div>
                <h1 className="text-2xl font-semibold mt-1">Enter 2FA code</h1>
              </div>
            </div>
            <p className="text-sm text-[#94a3b8]">Open your authenticator app and enter the 6-digit code for CyberSecurity4Charities.org.</p>
            <div>
              <label className="csc-label">6-digit code</label>
              <input
                required
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="csc-input text-center text-2xl tracking-[0.5em] font-mono-tech"
                placeholder="••••••"
                autoFocus
                data-testid="login-2fa-code"
              />
            </div>
            {error && <div className="text-sm text-red-400" data-testid="login-2fa-error">{error}</div>}
            <div className="flex gap-2">
              <button type="button" onClick={() => { setTicket(null); setCode(""); setError(""); }} className="csc-btn-ghost" data-testid="login-2fa-back">← Back</button>
              <button disabled={submitting || code.length !== 6} className="csc-btn-primary flex-1" data-testid="login-2fa-submit">
                {submitting ? "Verifying…" : "Verify & Sign In"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
